import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDaily, useDevices } from '@daily-co/daily-react-hooks';

export const DEVICE_STATE_LOADING = 'loading';
export const DEVICE_STATE_PENDING = 'pending';
export const DEVICE_STATE_ERROR = 'error';
export const DEVICE_STATE_GRANTED = 'granted';
export const DEVICE_STATE_NOT_FOUND = 'not-found';
export const DEVICE_STATE_NOT_SUPPORTED = 'not-supported';
export const DEVICE_STATE_BLOCKED = 'blocked';
export const DEVICE_STATE_IN_USE = 'in-use';
export const DEVICE_STATE_OFF = 'off';
export const DEVICE_STATE_PLAYABLE = 'playable';
export const DEVICE_STATE_SENDABLE = 'sendable';

export const MediaDeviceContext = createContext();

export const MediaDeviceProvider = ({ children }) => {
  const {
    hasCamError,
    cameras,
    camState,
    setCamera,
    hasMicError,
    microphones,
    micState,
    setMicrophone,
    speakers,
    setSpeaker,
    refreshDevices,
  } = useDevices();

  const daily = useDaily();
  const localParticipant = daily?.participants().local;

  const isCamMuted = useMemo(() => {
    const videoState = localParticipant?.tracks?.video?.state;
    return videoState === DEVICE_STATE_OFF || videoState === DEVICE_STATE_BLOCKED || hasCamError;
  }, [hasCamError, localParticipant?.tracks?.video?.state]);

  const [isMicGainMuted, setIsMicGainMuted] = useState(false);
  const gainNodeRef = useRef(null);
  const audioContextRef = useRef(null);

  const isMicMuted = useMemo(() => {
    if (isMicGainMuted) return true;
    const audioState = localParticipant?.tracks?.audio?.state;
    return audioState === DEVICE_STATE_OFF || audioState === DEVICE_STATE_BLOCKED || hasMicError;
  }, [hasMicError, localParticipant?.tracks?.audio?.state, isMicGainMuted]);

  const initGainNode = useCallback(async () => {
    if (gainNodeRef.current) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new AudioContext();
    const gainNode = audioContext.createGain();
    const source = audioContext.createMediaStreamSource(stream);
    const destination = audioContext.createMediaStreamDestination();
    source.connect(gainNode);
    gainNode.connect(destination);
    audioContextRef.current = audioContext;
    gainNodeRef.current = gainNode;
    await daily.setInputDevicesAsync({
      audioSource: destination.stream.getAudioTracks()[0],
    });
  }, [daily]);

  const toggleMic = useCallback(async () => {
    if (!daily) return;
    await initGainNode();
    const newMuted = !isMicGainMuted;
    gainNodeRef.current.gain.value = newMuted ? 0 : 1;
    setIsMicGainMuted(newMuted);
    // Gain mute leaves the Daily track "playable", so others learn about it via userData.
    const userData = daily.participants().local?.userData || {};
    daily.setUserData({ ...userData, micMuted: newMuted }).catch(() => { });
  }, [daily, isMicGainMuted, initGainNode]);

  return (
    <MediaDeviceContext.Provider
      value={{
        isCamMuted,
        isMicMuted,
        toggleMic,
        camError: hasCamError,
        cams: cameras,
        camState,
        micError: hasMicError,
        mics: microphones,
        micState,
        refreshDevices,
        setCurrentCam: setCamera,
        setCurrentMic: setMicrophone,
        setCurrentSpeaker: setSpeaker,
        speakers,
      }}
    >
      {children}
    </MediaDeviceContext.Provider>
  );
};

// MediaDeviceProvider.propTypes = {
//   children: PropTypes.node,
// };
// MediaDeviceProvider.defaultProps = {
//   children: null,
// };

export const useMediaDevices = () => useContext(MediaDeviceContext);
